import { buildingPrefabs } from './buildings.js';
import { BUILDING_PREFABS_EXTRA } from './buildings_extra.js';
import { restyle } from './buildings_restyle.js';
import { styledId } from './buildings_styled.js';
import { creaturePrefabs } from './creatures.js';
import { CREATURE_PREFABS_EXTRA } from './creatures_extra.js';
import { LEGEND_PREFABS } from './creatures_legend.js';
import { MERCHANT_PREFABS } from './creatures_merchant.js';
import { withDetail } from './detail.js';
import { withHiFiTraits } from './hifi.js';
import { BUILDING_STYLES, CULTURE_STYLE_IDS } from './styles.js';
// Prefab registry — singleton lookup for creature and building prefabs.
import type { BuildingStyleId } from './styles.js';
import type { BlockVoxel, CharacterPrefab, RegisteredPrefab, StructurePrefab } from './types.js';

const registry = new Map<string, RegisteredPrefab>();

// Register all creature prefabs.
for (const [id, base] of Object.entries(creaturePrefabs)) {
  const prefab = withHiFiTraits(id, withDetail(id, base));
  registry.set(id, { id, name: prefab.name, category: 'character', prefab });
}

// Register the bespoke prefabs authored for strategy-game unit kinds
// (settler, bronze_smith, engineer, guard, paladin, archmage, treant,
// chaos_lord) that previously fell back to placeholder mappings.
for (const [id, base] of Object.entries(CREATURE_PREFABS_EXTRA)) {
  const prefab = withHiFiTraits(id, withDetail(id, base));
  registry.set(id, { id, name: prefab.name, category: 'character', prefab });
}

// Foreign merchant vessels (sea.trade): one hull, painted by the house that
// owns it — the client supplies the house colour per instance.
for (const [id, base] of Object.entries(MERCHANT_PREFABS)) {
  const prefab = withHiFiTraits(id, withDetail(id, base));
  registry.set(id, { id, name: prefab.name, category: 'character', prefab });
}

// Champion prefabs (legends.enabled): player-owned re-skins of boss creatures.
for (const [id, base] of Object.entries(LEGEND_PREFABS)) {
  const prefab = withHiFiTraits(id, withDetail(id, base));
  registry.set(id, { id, name: prefab.name, category: 'character', prefab });
}

// Register all building prefabs.
for (const [id, prefab] of Object.entries(buildingPrefabs)) {
  registry.set(id, { id, name: prefab.name, category: 'structure', prefab });
}

// Register the bespoke prefabs authored for strategy-game building kinds
// (library, granary, well, tiered walls/towers, etc.) that previously
// fell back to placeholder mappings.
for (const [id, prefab] of Object.entries(BUILDING_PREFABS_EXTRA)) {
  registry.set(id, { id, name: prefab.name, category: 'structure', prefab });
}

// Every structure, the three drawn city centres included, gets a variant per
// culture (`workshop@nordic`, `castle@imperial`, ...) from the one restyle pipeline.
for (const id of [...Object.keys(buildingPrefabs), ...Object.keys(BUILDING_PREFABS_EXTRA)]) {
  for (const style of CULTURE_STYLE_IDS) {
    const styled = styledId(id, style);
    if (registry.has(styled)) continue;
    const baseEntry = registry.get(id);
    if (!baseEntry) continue;
    const prefab = restyle(baseEntry.prefab as StructurePrefab, BUILDING_STYLES[style], id);
    registry.set(styled, { id: styled, name: prefab.name, category: 'structure', prefab });
  }
}

// Dev-mode validation: catches the silent typo class (empty voxels, bad coords,
// missing colors, non-positive maxHp) before they render as black boxes or
// crash the InstancedMesh count=0 path. Production builds skip this entirely.
if (import.meta.env.DEV) {
  const problems: string[] = [];
  // Coordinate sanity bound — prefabs use 1/16 unit voxels; even huge boss
  // models stay within ±256 voxels per axis. Anything past that is a typo.
  const COORD_BOUND = 256;
  const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
  for (const [id, entry] of registry) {
    if (entry.category === 'character') {
      const prefab = entry.prefab as CharacterPrefab;
      if (prefab.maxHp <= 0) {
        problems.push(`[${id}] character prefab maxHp=${prefab.maxHp} (must be > 0)`);
      }
      const voxels: BlockVoxel[] = [];
      for (const part of prefab.parts) voxels.push(...part.voxels);
      if (voxels.length === 0) {
        problems.push(`[${id}] character prefab has no voxels across ${prefab.parts.length} parts`);
        continue;
      }
      for (const v of voxels) {
        if (!v.color || !HEX_COLOR_RE.test(v.color)) {
          problems.push(`[${id}] voxel (${v.x},${v.y},${v.z}) has invalid color "${v.color}"`);
        }
        if (
          Math.abs(v.x) > COORD_BOUND ||
          Math.abs(v.y) > COORD_BOUND ||
          Math.abs(v.z) > COORD_BOUND
        ) {
          problems.push(
            `[${id}] voxel coord out of bounds (±${COORD_BOUND}): (${v.x},${v.y},${v.z})`,
          );
        }
      }
    } else {
      const prefab = entry.prefab as StructurePrefab;
      if (prefab.blocks.length === 0) {
        problems.push(`[${id}] structure prefab has no blocks`);
        continue;
      }
      for (const b of prefab.blocks) {
        if (!b.color || !HEX_COLOR_RE.test(b.color)) {
          problems.push(`[${id}] block (${b.x},${b.y},${b.z}) has invalid color "${b.color}"`);
        }
        if (
          Math.abs(b.x) > COORD_BOUND ||
          Math.abs(b.y) > COORD_BOUND ||
          Math.abs(b.z) > COORD_BOUND
        ) {
          problems.push(
            `[${id}] block coord out of bounds (±${COORD_BOUND}): (${b.x},${b.y},${b.z})`,
          );
        }
      }
    }
  }
  if (problems.length > 0) {
    console.error(`[prefab registry] ${problems.length} validation problem(s) found:`);
    for (const p of problems) console.error(`  ${p}`);
  }
}

export function getCreaturePrefab(id: string): CharacterPrefab | undefined {
  const entry = registry.get(id);
  if (entry && entry.category === 'character') return entry.prefab as CharacterPrefab;
  return undefined;
}

/** Every registered creature (unit) prefab id, sorted — for tooling like the dev gallery. */
export function listCreaturePrefabIds(): string[] {
  return [...registry.values()]
    .filter((e) => e.category === 'character')
    .map((e) => e.id)
    .sort();
}

export function getBuildingPrefab(id: string): StructurePrefab | undefined {
  const entry = registry.get(id);
  if (entry && entry.category === 'structure') return entry.prefab as StructurePrefab;
  return undefined;
}

export function hasPrefab(id: string): boolean {
  return registry.has(id);
}

/** Maps strategy-game unit kind strings → creature prefab IDs. */
export const UNIT_KIND_TO_PREFAB: Record<string, string> = {
  warrior: 'soldier',
  archer: 'bowman',
  cavalry: 'knight',
  siege: 'catapult',
  healer: 'cleric',
  flyer: 'dragon',
  // Champion Dragon (legends.enabled) — the player-owned re-skin, not the boss.
  dragon: 'legend_dragon',
  // Champion Golem and Lich (legends.enabled; the sim's UnitKind::Golem / Lich).
  golem: 'legend_golem',
  lich: 'legend_lich',
  skeleton: 'skeleton',
  necromancer: 'lich',
  assassin: 'wraith',
  zealot: 'zealot',
  crusader: 'templar',
  inquisitor: 'sorcerer',
  blood_priest: 'blood_priest',
  templar: 'templar',
  death_knight: 'death_knight',
  heretic: 'banshee',
  dark_templar: 'dark_templar',
  luminarch: 'luminarch',
  dreadlord: 'warlord',
  wraith: 'wraith',
  seraphim: 'seraphim',
  zombie: 'zombie',
  slaver: 'bandit',
  supply_wagon: 'wagon',
  convoy: 'wagon',
  explorer: 'explorer',
  demon: 'demon',
  spy: 'spy',
  mage: 'mage',
  trade_ship: 'trade_ship',
  transport_ship: 'transport_ship',
  warship: 'warship',
  galleon: 'galleon',
  bronze_smith: 'bronze_smith',
  engineer: 'engineer',
  guard: 'guard',
  settler: 'settler',
  paladin: 'paladin',
  archmage: 'archmage',
  treant: 'treant',
  chaos_lord: 'chaos_lord',
};

/**
 * Unit kinds that render as ships. The renderer uses this to snap naval
 * units to the water surface elevation rather than the ground-plane y,
 * so trade_ships and warships float on ocean/coast tiles instead of
 * hovering at grass level.
 */
export const NAVAL_KINDS: ReadonlySet<string> = new Set([
  'trade_ship',
  'transport_ship',
  'warship',
  'galleon',
]);

/** Maps strategy-game building kind/type → building prefab IDs. */
export const BUILDING_KIND_TO_PREFAB: Record<string, string> = {
  // Unique prefabs
  workshop: 'workshop',
  market: 'market',
  farm: 'farm',
  temple: 'temple',
  church: 'church',
  barracks: 'barracks',
  fortress: 'fortress',
  iron_mine: 'iron_mine',
  silver_mine: 'silver_mine',
  gold_mine: 'gold_mine',
  crystal_mine: 'crystal_mine',
  dark_crystal_mine: 'dark_crystal_mine',
  harbour: 'harbour',
  shipyard: 'shipyard',
  dark_shrine: 'dark_shrine',
  mana_font: 'mana_font',
  grand_forge: 'grand_forge',
  observatory: 'observatory',
  portal_light: 'portal_light',
  portal_dark: 'portal_dark',
  // Reuse prefabs for generic buildings
  assassin_guild: 'assassin_guild',
  bank: 'bank',
  brewery: 'brewery',
  forge_hut: 'forge_hut',
  granary: 'granary',
  grand_shipyard: 'grand_shipyard',
  herbalist_hut: 'herbalist_hut',
  library: 'library',
  mine: 'mine',
  pasture: 'pasture',
  siege_workshop: 'siege_workshop',
  slave_market: 'slave_market',
  well: 'well',
  winery: 'winery',
  // Tiered defensive structures
  tower_1: 'tower_1',
  tower_2: 'tower_2',
  tower_3: 'tower_3',
  walls_1: 'walls_1',
  walls_2: 'walls_2',
  walls_3: 'walls_3',
  // Social / cultural
  theater: 'theater',
  colosseum: 'colosseum',
  // Settlement tiers
  capital: 'castle',
  village: 'house',
  town: 'tavern',
  city: 'castle',
};

/** City-centre kind by settlement tier: 1 village / outpost, 2 town, 3+ capital. */
export function cityCentreKind(tier: number): 'village' | 'town' | 'capital' {
  return tier >= 3 ? 'capital' : tier === 2 ? 'town' : 'village';
}

/**
 * Prefab id for a building kind in a player type's style. Falls back to the
 * shared prefab when that style has no variant (neutral, unknown style).
 */
export function resolveBuildingPrefabId(kind: string, style: BuildingStyleId = 'neutral'): string {
  const base = BUILDING_KIND_TO_PREFAB[kind] ?? 'house';
  const styled = styledId(base, style);
  return registry.has(styled) ? styled : base;
}
